import { useState, useCallback, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@medikei/ui';
import { Button } from '@/components/ui/Button';
import {
  ArrowLeft,
  Search,
  X,
  FileText,
  Loader2,
  Pill,
  Syringe,
  AlertCircle,
  AlertTriangle,
  ShieldCheck,
  CheckCircle2,
  Copy,
  Check,
  ExternalLink,
  RefreshCw,
  Calendar,
  Phone,
  Lock,
  Sparkles,
} from 'lucide-react';
import {
  doctorsApi,
  medicalRecordsApi,
  memedApi,
  patientsApi,
  type SyncMemedPrescriptionDto,
} from '@/api';
import { useAuth } from '@/contexts/AuthContext';
import { useDoctorProfile } from '@/hooks/doctor/useDoctorProfile';
import {
  useMemedPrescription,
  extractMemedPrescriptionData,
  type MemedRichMedication,
} from '@/hooks/useMemedPrescription';
import { useMemedConfig } from '@/hooks/useMemedConfig';
import { mapPatientToMemed, validateMemedPatient } from '@/hooks/memed/memed-patient.mapper';
import { obterWorkplaceParaAtendimento } from '@/hooks/memed/workplace-router';
import { CLINICAL_KITS } from '@/hooks/memed/memedKits.service';
import { verificarRiscoPolifarmacia } from '@/services/polifarmaciaRadar.service';
import { useToast } from '@/providers/ToastProvider';

interface Patient {
  id: string;
  name: string;
  email: string;
  phone?: string;
  cpf: string;
  birthDate: string;
  gender?: string;
  allergies?: string[] | Array<{ name: string; memedId?: number }>;
  chronicConditions?: string[] | Array<{ name: string; cid10?: string }>;
  healthProfile?: any;
}

type LocalPrescriptionType = 'COMUM' | 'CONTROLADA';

interface IssuedPrescriptionState {
  prescriptionId?: string;
  memedPrescriptionId: string;
  medications: MemedRichMedication[];
  digitalPrescriptionLink?: string;
  unlockCode?: string;
  signedAt?: string;
}

function formatCpf(cpf?: string): string {
  if (!cpf) return '';
  const digits = cpf.replace(/\D/g, '');
  if (digits.length !== 11) return cpf;
  return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
}

function formatPhone(phone?: string): string {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 11) {
    return digits.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  }
  if (digits.length === 10) {
    return digits.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  }
  return phone;
}

function formatBirthDate(dateStr?: string): string {
  if (!dateStr) return '';
  const clean = dateStr.includes('T') ? (dateStr.split('T')[0] || '') : dateStr;
  if (clean.includes('-')) {
    const parts = clean.split('-');
    const [y, m, d] = parts;
    if (parts.length === 3 && y && m && d && y.length === 4) {
      return `${d.padStart(2, '0')}/${m.padStart(2, '0')}/${y}`;
    }
  }
  return dateStr;
}

export function DoctorNewPrescriptionPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { toast } = useToast();
  const { data: doctorProfile } = useDoctorProfile(user?.id || '');
  const doctorId = doctorProfile?.id || user?.id || '';

  const patientIdParam = searchParams.get('patientId');
  const appointmentIdParam = searchParams.get('appointmentId');
  const typeParam = searchParams.get('type');

  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [prescriptionType, setPrescriptionType] = useState<LocalPrescriptionType>(
    typeParam === 'controlada' ? 'CONTROLADA' : 'COMUM'
  );
  const [additionalInstructions, setAdditionalInstructions] = useState('');
  const [hasControlledMedicationInCanvas, setHasControlledMedicationInCanvas] = useState(false);
  const [, setLiveCanvasMeds] = useState<Map<string, any>>(new Map());
  const [patientSearch, setPatientSearch] = useState('');
  const [showPatientDropdown, setShowPatientDropdown] = useState(false);
  const [isOpeningPrescription, setIsOpeningPrescription] = useState(false);
  const [isInitializingMemed, setIsInitializingMemed] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [issuedPrescription, setIssuedPrescription] = useState<IssuedPrescriptionState | null>(null);
  const [copiedLink, setCopiedLink] = useState(false);
  const [isMemedOpen, setIsMemedOpen] = useState(false);

  const { openPrescription, onMemedEvent, isScriptLoaded, isModuleReady, isSettingPatient, isSettingAllergies, loadScript } =
    useMemedPrescription();
  const { data: memedConfig } = useMemedConfig();

  const { data: patientsData } = useQuery({
    queryKey: ['doctor', 'patients', 1, 100],
    queryFn: async () => {
      const response = await doctorsApi.getMyPatients(1, 100);
      return response.patients;
    },
    enabled: !patientIdParam,
  });

  const preSelectedPatient = useQuery({
    queryKey: ['doctor', 'patient', patientIdParam],
    queryFn: async () => {
      if (!patientIdParam) return null;
      const patients = await doctorsApi.getMyPatients(1, 100, '').then((r) => r.patients);
      return patients.find((p) => p.id === patientIdParam) || null;
    },
    enabled: !!patientIdParam,
  });

  useEffect(() => {
    if (preSelectedPatient.data) {
      setSelectedPatient(preSelectedPatient.data);
    }
  }, [preSelectedPatient.data]);

  // Carrega alergias adicionais do paciente via API se disponível
  const { data: patientAllergies = [] } = useQuery({
    queryKey: ['patient', selectedPatient?.id, 'allergies'],
    queryFn: async () => {
      if (!selectedPatient?.id) return [];
      try {
        if (patientsApi?.getPatientAllergies) {
          return await patientsApi.getPatientAllergies(selectedPatient.id);
        }
        return [];
      } catch {
        return [];
      }
    },
    enabled: !!selectedPatient?.id,
  });

  const filteredPatients = useMemo(() => {
    if (!patientsData) return [];
    if (!patientSearch.trim()) return patientsData;
    const search = patientSearch.toLowerCase();
    return patientsData.filter(
      (p) =>
        p.name.toLowerCase().includes(search) ||
        p.email.toLowerCase().includes(search) ||
        p.cpf.includes(search)
    );
  }, [patientsData, patientSearch]);

  const missingPatientFields = useMemo(() => {
    if (!selectedPatient) return [];
    const missing: string[] = [];
    if (!selectedPatient.name?.trim()) missing.push('Nome completo');
    if (!selectedPatient.cpf?.trim()) missing.push('CPF (obrigatório para emissão ICP-Brasil)');
    if (!selectedPatient.birthDate?.trim()) missing.push('Data de nascimento');
    return missing;
  }, [selectedPatient]);

  const formattedAllergies = useMemo(() => {
    const result: Array<{ name: string; memedId?: number }> = [];
    if (Array.isArray(selectedPatient?.allergies)) {
      selectedPatient.allergies.forEach((a: any) => {
        if (typeof a === 'string' && a.trim()) result.push({ name: a.trim() });
        else if (a?.name || a?.allergen) result.push({ name: a.name || a.allergen, memedId: a.memedId });
      });
    }
    if (Array.isArray(patientAllergies)) {
      patientAllergies.forEach((a) => {
        const name = a.allergen || (a as any).name;
        if (name && !result.some((r) => r.name.toLowerCase() === name.toLowerCase())) {
          result.push({ name, memedId: a.memedId ?? undefined });
        }
      });
    }
    return result;
  }, [selectedPatient?.allergies, patientAllergies]);

  const formattedConditions = useMemo(() => {
    const result: Array<{ name: string; cid10?: string }> = [];
    if (Array.isArray(selectedPatient?.chronicConditions)) {
      selectedPatient.chronicConditions.forEach((c: any) => {
        if (typeof c === 'string' && c.trim()) result.push({ name: c.trim() });
        else if (c?.name) result.push({ name: c.name, cid10: c.cid10 });
      });
    }
    return result;
  }, [selectedPatient?.chronicConditions]);

  const handleSelectPatient = useCallback((patient: Patient) => {
    setSelectedPatient(patient);
    setPatientSearch('');
    setShowPatientDropdown(false);
  }, []);

  const syncPrescriptionMutation = useMutation({
    mutationFn: async (payload: SyncMemedPrescriptionDto) => {
      return medicalRecordsApi.syncMemedPrescription(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['doctor', 'prescriptions'] });
      queryClient.invalidateQueries({ queryKey: ['medical-records'] });
      toast({
        title: 'Prescrição emitida e assinada!',
        description: 'A receita foi assinada com ICP-Brasil e salva no prontuário do paciente.',
        variant: 'success',
      });
    },
    onError: (error) => {
      toast({
        title: 'Erro ao sincronizar',
        description: error instanceof Error ? error.message : 'Não foi possível sincronizar os dados da prescrição.',
        variant: 'error',
      });
    },
  });

  useEffect(() => {
    const unsubscribePrinted = onMemedEvent('prescricaoImpressa', async (data) => {
      const extracted = extractMemedPrescriptionData(data);
      const memedId = extracted.memedPrescriptionId;

      if (memedId && selectedPatient) {
        try {
          const syncResult = await syncPrescriptionMutation.mutateAsync({
            appointmentId: appointmentIdParam || undefined,
            medicalRecordId: appointmentIdParam || undefined,
            patientId: selectedPatient.id,
            doctorId,
            memedPrescriptionId: memedId,
            medications: extracted.medications,
            digitalPrescriptionLink: extracted.digitalPrescriptionLink,
            unlockCode: extracted.unlockCode,
          });

          let digitalPrescriptionLink = extracted.digitalPrescriptionLink;
          let unlockCode = extracted.unlockCode;

          if (!digitalPrescriptionLink || !unlockCode) {
            try {
              const linkRes = await medicalRecordsApi.getDigitalPrescriptionLink(syncResult?.id || memedId);
              if (linkRes?.link) digitalPrescriptionLink = linkRes.link;
              if (linkRes?.unlockCode) unlockCode = linkRes.unlockCode;
            } catch {
              // Usa os dados existentes do evento
            }
          }

          setIssuedPrescription({
            prescriptionId: syncResult?.id,
            memedPrescriptionId: memedId,
            medications: extracted.medications,
            digitalPrescriptionLink,
            unlockCode,
            signedAt: new Date().toISOString(),
          });

          // Ingestão no BI Farmacoeconômico
          if (typeof medicalRecordsApi.ingestPrescriptionAnalytics === 'function') {
            try {
              await medicalRecordsApi.ingestPrescriptionAnalytics({
                prescriptionUuid: memedId,
                pacienteId: selectedPatient.id,
                medicoId: doctorId,
                medicamentos: (data as any)?.prescrição?.medicamentos || extracted.medications,
              });
            } catch (analyticsErr) {
              console.warn('[DoctorNewPrescriptionPage] Falha na ingestão analytics:', analyticsErr);
            }
          }

          // Ingestão de atestado se emitido em conjunto com a prescrição
          const mentions = (data as any)?.prescrição?.additionalData?.mentions || (data as any)?.additionalData?.mentions;
          const hasAtestado =
            ((data as any)?.prescrição?.medicamentos || []).some(
              (m: any) => m.tipo === 'custom' && m.custom_categoria === 'atestado',
            ) || Boolean(mentions);

          if (hasAtestado && selectedPatient) {
            try {
              const docToken = await memedApi.getToken().then((r) => r.token).catch(() => undefined);
              const finalDays = Number(mentions?.dias_afastamento) || 1;
              const finalCid = mentions?.CID || mentions?.cid || undefined;
              await memedApi.syncMemedCertificate({
                appointmentId: appointmentIdParam || undefined,
                patientId: selectedPatient.id,
                doctorId,
                memedPrescriptionId: memedId,
                diagnosis: finalCid ? `CID ${finalCid}` : 'Atestado Médico',
                totalDays: finalDays,
                diasAfastamento: finalDays,
                cidCodigo: finalCid,
                tipoDocumento: 'TRABALHO',
                doctorToken: docToken,
                pdfUrl: (data as any)?.pdfUrl,
                assinadoDigitalmente: true,
                mentions,
              });
              queryClient.invalidateQueries({ queryKey: ['doctor', 'certificates'] });
            } catch (certErr) {
              console.warn('[DoctorNewPrescriptionPage] Falha ao sincronizar atestado conjunto:', certErr);
            }
          }
        } catch (err) {
          console.error('[DoctorNewPrescriptionPage] Erro ao sincronizar prescrição MeMed:', err);
        }
      }
    });

    const unsubscribeDeleted = onMemedEvent('prescricaoExcluida', async (idPrescricao) => {
      console.warn('[DoctorNewPrescriptionPage] Prescrição excluída na MeMed:', idPrescricao);
      try {
        await medicalRecordsApi.markMemedPrescriptionExcluded(String(idPrescricao));
        queryClient.invalidateQueries({ queryKey: ['doctor', 'prescriptions'] });
        setIssuedPrescription(null);
        toast({
          title: 'Prescrição excluída',
          description: 'A prescrição foi cancelada com sucesso no prontuário.',
          variant: 'info',
        });
      } catch (err) {
        console.error('Falha ao sincronizar exclusão da prescrição MeMed:', err);
      }
    });

    const unsubscribeAdded = onMemedEvent('medicamentoAdicionado', (medicine: any) => {
      setLiveCanvasMeds((prev) => {
        const next = new Map(prev);
        next.set(medicine.id, medicine);
        const hasControlled = Array.from(next.values()).some((m) => m.controle_especial);
        setHasControlledMedicationInCanvas(hasControlled);
        return next;
      });

      if (medicine.controle_especial) {
        toast({
          title: 'Medicamento de Controle Especial inserido',
          description: 'Confirme se o endereço completo e CPF do paciente estão preenchidos para emissão válida em 2 vias.',
          variant: 'warning',
        });
      }

      // Radar de Polifarmácia em tempo real
      const alertaPolifarmacia = verificarRiscoPolifarmacia(
        medicine.nome,
        (selectedPatient?.chronicConditions as any) || [],
      );
      if (alertaPolifarmacia) {
        toast({
          title: 'Alerta de Farmacovigilância / Polifarmácia',
          description: alertaPolifarmacia.mensagem,
          variant: 'warning',
        });
      }
    });

    const unsubscribeRemoved = onMemedEvent('medicamentoRemovido', (medicineId: string) => {
      setLiveCanvasMeds((prev) => {
        const next = new Map(prev);
        next.delete(medicineId);
        const hasControlled = Array.from(next.values()).some((m) => m.controle_especial);
        setHasControlledMedicationInCanvas(hasControlled);
        return next;
      });
    });

    const unsubscribeHide = onMemedEvent('core:moduleHide', () => {
      console.info('[DoctorNewPrescriptionPage] Módulo MeMed fechado pelo prescritor.');
      setIsMemedOpen(false);
      if (typeof document !== 'undefined') {
        document.body.classList.remove('memed-module-open');
      }
    });

    return () => {
      unsubscribePrinted();
      unsubscribeDeleted();
      unsubscribeAdded();
      unsubscribeRemoved();
      unsubscribeHide();
    };
  }, [onMemedEvent, selectedPatient, appointmentIdParam, doctorId, syncPrescriptionMutation, queryClient, toast]);

  const handleOpenMemed = useCallback(async (customItems?: any[]) => {
    if (!selectedPatient) {
      setLocalError('Selecione um paciente');
      return;
    }

    console.info('[DoctorNewPrescriptionPage] [MEMED_DIAGNOSTIC] Clique em Emitir Receita. Paciente selecionado:', selectedPatient.name);

    if (missingPatientFields.length > 0) {
      console.warn('[DoctorNewPrescriptionPage] [MEMED_DIAGNOSTIC] Campos cadastrais pendentes:', missingPatientFields);
      setLocalError(
        `Dados cadastrais obrigatórios pendentes para emissão MeMed: ${missingPatientFields.join(', ')}. Os dados completos do paciente são obrigatórios para a emissão da receita digital na MeMed com valor legal ICP-Brasil.`
      );
      return;
    }

    setLocalError(null);

    // Inicialização sob demanda do script/token se necessário
    let activeDoctorToken: string | undefined;
    if (!isScriptLoaded || !isModuleReady) {
      console.info('[DoctorNewPrescriptionPage] [MEMED_DIAGNOSTIC] Módulo MeMed não inicializado. Obtendo token e carregando script...');
      setIsInitializingMemed(true);
      let token: string | undefined;
      try {
        const res = await memedApi.getToken();
        if (res?.token) {
          token = res.token;
          activeDoctorToken = res.token;
          console.info('[DoctorNewPrescriptionPage] [MEMED_DIAGNOSTIC] ✓ Token MeMed recebido');
        }
      } catch (tokenErr: any) {
        const errorMsg = tokenErr?.message || 'Médico não está vinculado à MeMed';
        console.error('[DoctorNewPrescriptionPage] [MEMED_DIAGNOSTIC] ❌ Erro ao obter token MeMed:', errorMsg);
        setLocalError(
          `${errorMsg}. Conecte seu cadastro de médico com a MeMed em Configurações > MeMed para habilitar a emissão com certificado digital ICP-Brasil.`
        );
        setIsInitializingMemed(false);
        return;
      }

      if (!token) {
        setLocalError(
          'Token da MeMed não encontrado. Conecte seu perfil em Configurações > MeMed.'
        );
        setIsInitializingMemed(false);
        return;
      }

      let scriptSuccess = false;
      try {
        scriptSuccess = await loadScript(token);
      } catch (err) {
        console.error('[DoctorNewPrescriptionPage] [MEMED_DIAGNOSTIC] ❌ Erro ao inicializar MeMed:', err);
      } finally {
        setIsInitializingMemed(false);
      }

      if (!scriptSuccess) {
        setLocalError('Não foi possível carregar o módulo oficial da MeMed. Verifique sua conexão e tente novamente.');
        return;
      }
    }

    setIsOpeningPrescription(true);

    const memedPatient = mapPatientToMemed({
      id: selectedPatient.id,
      name: selectedPatient.name,
      gender: selectedPatient.gender,
      cpf: selectedPatient.cpf,
      birthDate: selectedPatient.birthDate,
      phone: selectedPatient.phone,
      email: selectedPatient.email,
      healthProfile: null,
    } as any);

    const validation = validateMemedPatient(memedPatient);
    if (!validation.valid) {
      console.warn('[DoctorNewPrescriptionPage] [MEMED_DIAGNOSTIC] Validação de dados MeMed do paciente falhou:', validation.missingFields);
      setLocalError(`Dados obrigatórios faltando: ${validation.missingFields.join(', ')}`);
      setIsOpeningPrescription(false);
      return;
    }

    try {
      console.info('[DoctorNewPrescriptionPage] [MEMED_DIAGNOSTIC] Abrindo prescrição MeMed...');
      const effectiveWorkplace = obterWorkplaceParaAtendimento(
        searchParams.get('serviceType') || 'TELEMEDICINA',
        memedConfig?.workplace,
      );

      const result = await openPrescription(
        memedPatient,
        effectiveWorkplace,
        formattedAllergies as any,
        formattedConditions as any,
        {
          appointmentId: appointmentIdParam || undefined,
        },
        {
          receituarioTheme: prescriptionType === 'CONTROLADA' ? 2 : 1,
          token: activeDoctorToken,
          items: customItems,
          additionalData: {
            header: [
              ...(additionalInstructions.trim() ? [{ Observações: additionalInstructions }] : []),
              { Atendimento: `Teleconsulta Medikei nº ${appointmentIdParam || 'DIGITAL'}` },
              { Regulamentação: 'Resolução CFM nº 2.314/2022' },
              { Validação: 'Documento com assinatura digital ICP-Brasil e carimbo de tempo oficial' },
            ],
          },
          featureToggles: {
            forceSign: false,
            buttonClose: true,
            editIdentification: false,
            deletePatient: false,
            removePatient: false,
            editPatient: false,
            dropdownSync: false,
            allowShareModal: false,
            conclusionModalEdit: false,
            copyMedicalRecords: true,
            historyPrescription: false,
          },
        }
      );

      console.info('[DoctorNewPrescriptionPage] [MEMED_DIAGNOSTIC] Resultado openPrescription:', result);

      if (!result.success) {
        setLocalError(result.error || 'Erro ao abrir prescrição');
        return;
      }

      setIsMemedOpen(true);
    } catch (err) {
      console.error('[DoctorNewPrescriptionPage] [MEMED_DIAGNOSTIC] ❌ Erro ao abrir prescrição:', err);
      setLocalError(err instanceof Error ? err.message : 'Erro ao abrir prescrição');
    } finally {
      setIsOpeningPrescription(false);
    }
  }, [
    selectedPatient,
    missingPatientFields,
    isScriptLoaded,
    isModuleReady,
    loadScript,
    openPrescription,
    memedConfig,
    formattedAllergies,
    formattedConditions,
    appointmentIdParam,
    prescriptionType,
    additionalInstructions,
  ]);

  const handleCopyLink = useCallback(() => {
    if (issuedPrescription?.digitalPrescriptionLink) {
      navigator.clipboard?.writeText(issuedPrescription.digitalPrescriptionLink);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2500);
    }
  }, [issuedPrescription?.digitalPrescriptionLink]);

  const handleResetToNew = useCallback(() => {
    setIssuedPrescription(null);
    setAdditionalInstructions('');
  }, []);

  const isDisabled = isOpeningPrescription || isSettingPatient || isSettingAllergies || isInitializingMemed;

  return (
    <div className="p-4 sm:p-6 space-y-5 sm:space-y-6 max-w-4xl mx-auto">
      {/* Top navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate(appointmentIdParam ? `/medico/consultas/${appointmentIdParam}` : '/medico/receitas')}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors text-sm font-medium"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>{appointmentIdParam ? 'Voltar à Consulta' : 'Voltar às Receitas'}</span>
        </button>
      </div>

      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-2xl bg-emerald-100 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-400 shrink-0">
          <Pill className="h-6 w-6" />
        </div>
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-foreground">Nova Prescrição Digital</h1>
          <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
            Fluxo Sinapse-First integrado à MeMed com assinatura digital e validade ICP-Brasil
          </p>
        </div>
      </div>

      <div className="w-full h-px bg-border" />

      {/* Render Issued Prescription Confirmation Screen when emission is completed */}
      {issuedPrescription ? (
        <div className="space-y-6">
          {/* Success Hero Card */}
          <Card className="border-emerald-200 bg-emerald-50/50 shadow-sm">
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-emerald-100 text-emerald-700 rounded-2xl flex-shrink-0">
                    <CheckCircle2 className="h-8 w-8" />
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-600 text-white shadow-sm">
                        <ShieldCheck className="h-4 w-4" />
                        ICP-Brasil Assinada
                      </span>
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 border border-blue-200">
                        MeMed Sinapse
                      </span>
                    </div>
                    <h2 className="text-xl font-bold text-gray-900">Prescrição Emitida e Assinada com Sucesso!</h2>
                    <p className="text-sm text-gray-600">
                      Receita digital assinada com validade jurídica nacional para o paciente{' '}
                      <strong className="text-gray-900">{selectedPatient?.name}</strong>
                      {selectedPatient?.cpf ? ` (${formatCpf(selectedPatient.cpf)})` : ''}.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Digital Link & Unlock Code (PIN) Card */}
          <Card className="border-gray-200 shadow-sm">
            <CardContent className="p-6 space-y-4">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <Lock className="h-4 w-4 text-emerald-600" />
                Dados de Acesso da Receita Digital
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* PIN Box */}
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl space-y-2">
                  <span className="text-xs font-semibold text-gray-500 block uppercase tracking-wider">
                    PIN / Código de Desbloqueio
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-2xl font-extrabold tracking-widest text-emerald-700 bg-white border border-emerald-200 px-4 py-1.5 rounded-lg shadow-inner">
                      {issuedPrescription.unlockCode || '••••'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500">
                    O paciente e o farmacêutico utilizam este código para dispensação oficial dos medicamentos.
                  </p>
                </div>

                {/* Digital Link Box */}
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl space-y-2">
                  <span className="text-xs font-semibold text-gray-500 block uppercase tracking-wider">
                    Link da Receita Digital
                  </span>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      readOnly
                      value={issuedPrescription.digitalPrescriptionLink || 'Link gerado na MeMed'}
                      className="flex-1 text-xs bg-white border border-gray-300 rounded-lg px-3 py-2 text-gray-700 select-all"
                    />
                    {issuedPrescription.digitalPrescriptionLink && (
                      <button
                        type="button"
                        onClick={handleCopyLink}
                        className="inline-flex items-center gap-1.5 px-3 py-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 rounded-lg text-xs font-semibold transition-colors"
                      >
                        {copiedLink ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                        <span>{copiedLink ? 'Copiado!' : 'Copiar Link'}</span>
                      </button>
                    )}
                  </div>
                  {issuedPrescription.digitalPrescriptionLink && (
                    <button
                      type="button"
                      onClick={() => window.open(issuedPrescription.digitalPrescriptionLink, '_blank')}
                      className="inline-flex items-center gap-1.5 text-xs text-emerald-700 hover:text-emerald-800 font-semibold"
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                      <span>Abrir PDF Oficial</span>
                    </button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Prescribed Medications Rich List */}
          <Card className="border-gray-200 shadow-sm">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                  <Pill className="h-4 w-4 text-blue-600" />
                  Medicamentos Prescritos ({issuedPrescription.medications.length})
                </h3>
                <span className="text-xs text-gray-500">Base oficial MeMed</span>
              </div>

              {issuedPrescription.medications.length === 0 ? (
                <p className="text-sm text-gray-500 italic py-2">
                  Prescrição registrada com identificador MeMed #{issuedPrescription.memedPrescriptionId}.
                </p>
              ) : (
                <div className="divide-y divide-gray-100">
                  {issuedPrescription.medications.map((med, idx) => (
                    <div key={med.id || idx} className="py-3 flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-gray-900 text-sm">{med.nome}</span>
                          {med.controle_especial && (
                            <span className="px-2 py-0.5 text-[11px] font-medium bg-purple-100 text-purple-800 rounded-full border border-purple-200">
                              Controle Especial
                            </span>
                          )}
                          {med.fabricante && (
                            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                              {med.fabricante}
                            </span>
                          )}
                        </div>
                        {(med.descricao || med.principio_ativo) && (
                          <p className="text-xs text-gray-500">{med.principio_ativo || med.descricao}</p>
                        )}
                        {(med.sanitized_posology || med.posologia) && (
                          <p className="text-xs text-gray-700 bg-blue-50/60 border border-blue-100 rounded-lg p-2 mt-1">
                            <strong className="text-blue-900">Posologia: </strong>
                            {med.sanitized_posology || med.posologia}
                          </p>
                        )}
                      </div>
                      {med.quantidade !== undefined && (
                        <div className="text-xs font-medium text-gray-600 sm:text-right flex-shrink-0">
                          <span>Qtd: {med.quantidade} {med.unidade || ''}</span>
                  </div>
                )}

                {/* Alerta de Controle Especial em Tempo Real */}
                {hasControlledMedicationInCanvas && (
                  <div className="flex items-start gap-3 p-4 bg-purple-50 border border-purple-200 rounded-xl text-purple-900 shadow-sm">
                    <AlertTriangle className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
                    <div className="space-y-1 text-xs">
                      <p className="font-semibold text-purple-950">
                        Item de Controle Especial Inserido na Receita
                      </p>
                      <p>
                        A emissão em 2 vias (Notificação de Receita / Controle Especial) exige CPF e endereço completo do paciente no documento para dispensação farmacêutica válida.
                      </p>
                    </div>
                  </div>
                )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Action buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button
              type="button"
              onClick={handleResetToNew}
              className="flex-1 flex items-center justify-center gap-2 px-5 py-3 border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 font-semibold rounded-xl transition-colors shadow-sm"
            >
              <RefreshCw className="h-4 w-4" />
              <span>Emitir Nova Receita</span>
            </button>
            <button
              type="button"
              onClick={() => navigate(appointmentIdParam ? `/medico/consultas/${appointmentIdParam}` : '/medico/receitas')}
              className="flex-1 flex items-center justify-center gap-2 px-5 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl transition-colors shadow-sm"
            >
              <FileText className="h-4 w-4" />
              <span>Voltar ao Prontuário / Dashboard</span>
            </button>
          </div>
        </div>
      ) : (
        /* Form View (Patient Selection, Safety Data, Type Selector, Hero Trigger) */
        <div className="space-y-5 sm:space-y-6">
          {/* Patient Selection / Patient Identification Card */}
          <div className="space-y-2">
            <label className="text-sm font-semibold text-foreground">Paciente</label>
            {selectedPatient ? (
              <div className="space-y-3">
                {/* Patient Safety and Identification Card */}
                <Card className="border-border bg-card shadow-sm overflow-hidden">
                  <CardContent className="p-4 sm:p-5 space-y-4">
                    <div className="flex items-center justify-between pb-3 border-b border-border">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-2xl bg-gradient-to-tr from-blue-600 to-indigo-600 text-white flex items-center justify-center font-bold text-base sm:text-lg shadow-sm shrink-0">
                          {selectedPatient.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <h3 className="font-bold text-foreground text-sm sm:text-base truncate">{selectedPatient.name}</h3>
                          <p className="text-xs text-muted-foreground truncate">{selectedPatient.email}</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setSelectedPatient(null)}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-lg transition-colors border border-transparent hover:border-destructive/20 shrink-0 cursor-pointer"
                        title="Trocar paciente"
                      >
                        <X className="h-3.5 w-3.5" />
                        <span className="hidden sm:inline">Trocar</span>
                      </button>
                    </div>

                    {/* Patient Details Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3 text-xs">
                      <div className="p-3 bg-muted/40 border border-border rounded-xl space-y-0.5">
                        <span className="text-muted-foreground flex items-center gap-1 font-medium text-[11px]">
                          <ShieldCheck className="h-3.5 w-3.5 text-primary" />
                          CPF (ICP-Brasil)
                        </span>
                        <p className={`font-semibold ${selectedPatient.cpf ? 'text-foreground' : 'text-amber-600 dark:text-amber-400'}`}>
                          {selectedPatient.cpf ? formatCpf(selectedPatient.cpf) : 'Não informado'}
                        </p>
                      </div>

                      <div className="p-3 bg-muted/40 border border-border rounded-xl space-y-0.5">
                        <span className="text-muted-foreground flex items-center gap-1 font-medium text-[11px]">
                          <Calendar className="h-3.5 w-3.5 text-primary" />
                          Data de Nascimento
                        </span>
                        <p className={`font-semibold ${selectedPatient.birthDate ? 'text-foreground' : 'text-amber-600 dark:text-amber-400'}`}>
                          {selectedPatient.birthDate ? formatBirthDate(selectedPatient.birthDate) : 'Não informada'}
                        </p>
                      </div>

                      <div className="p-3 bg-muted/40 border border-border rounded-xl space-y-0.5">
                        <span className="text-muted-foreground flex items-center gap-1 font-medium text-[11px]">
                          <Phone className="h-3.5 w-3.5 text-primary" />
                          Telefone / SMS
                        </span>
                        <p className="font-semibold text-foreground">
                          {selectedPatient.phone ? formatPhone(selectedPatient.phone) : 'Não informado'}
                        </p>
                      </div>

                      <div className="p-3 bg-muted/40 border border-border rounded-xl space-y-1">
                        <span className="text-muted-foreground flex items-center gap-1 font-medium text-[11px]">
                          <AlertCircle className="h-3.5 w-3.5 text-primary" />
                          Segurança Clínica
                        </span>
                        <div className="flex flex-wrap gap-1">
                          {formattedAllergies.length > 0 ? (
                            formattedAllergies.slice(0, 2).map((a, i) => (
                              <span key={i} className="px-1.5 py-0.5 bg-red-100 dark:bg-red-950/60 text-red-800 dark:text-red-300 rounded text-[10px] font-medium">
                                {a.name}
                              </span>
                            ))
                          ) : (
                            <span className="text-emerald-700 dark:text-emerald-400 font-medium text-[11px]">Sem alergias registradas</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Missing Patient Required Fields Alert for ICP-Brasil */}
                {missingPatientFields.length > 0 && (
                  <div className="flex items-start gap-3 p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl text-amber-800 dark:text-amber-300 shadow-sm">
                    <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                    <div className="space-y-1 text-xs">
                      <p className="font-semibold text-foreground">
                        Aviso: Dados do paciente incompletos para emissão na MeMed
                      </p>
                      <p className="leading-relaxed">
                        Para a emissão de receita digital com assinatura e valor legal ICP-Brasil, os dados cadastrais do paciente são obrigatórios.
                        Campos pendentes: <strong className="text-foreground">{missingPatientFields.join(', ')}</strong>.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="relative">
                <div className="relative">
                  <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Buscar paciente por nome, email ou CPF..."
                    value={patientSearch}
                    onChange={(e) => {
                      setPatientSearch(e.target.value);
                      setShowPatientDropdown(true);
                    }}
                    onFocus={() => setShowPatientDropdown(true)}
                    className="w-full pl-10 pr-4 py-2.5 sm:py-3 border border-border bg-background text-foreground rounded-xl focus:outline-none focus:ring-2 focus:ring-primary shadow-xs text-sm"
                  />
                </div>
                {showPatientDropdown && filteredPatients.length > 0 && (
                  <div className="absolute z-20 w-full mt-1.5 bg-card border border-border rounded-xl shadow-lg max-h-60 overflow-auto divide-y divide-border">
                    {filteredPatients.slice(0, 10).map((patient) => (
                      <button
                        key={patient.id}
                        type="button"
                        onClick={() => handleSelectPatient(patient)}
                        className="w-full flex items-center gap-3 p-3 hover:bg-muted/50 transition-colors text-left"
                      >
                        <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-xs shrink-0">
                          {patient.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-foreground truncate">{patient.name}</p>
                          <p className="text-xs text-muted-foreground truncate">
                            {patient.cpf ? formatCpf(patient.cpf) : 'CPF pendente'} • {patient.email}
                          </p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Kits Clínicos a 1 Clique */}
            {selectedPatient && (
              <div className="space-y-2.5 pt-1">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                    <Sparkles className="h-4 w-4 text-amber-500" />
                    Kits Clínicos Padronizados Medikei (1 Clique)
                  </label>
                  <span className="text-xs text-muted-foreground hidden sm:inline">Injeção estruturada no canvas</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {Object.values(CLINICAL_KITS).map((kit) => (
                    <button
                      key={kit.key}
                      type="button"
                      onClick={() => handleOpenMemed(kit.itens)}
                      disabled={isDisabled}
                      className="p-3.5 bg-muted/30 dark:bg-neutral-800/50 hover:bg-emerald-50/60 dark:hover:bg-emerald-950/30 border border-border hover:border-emerald-300 dark:hover:border-emerald-700 rounded-xl text-left transition-all group flex flex-col justify-between shadow-2xs cursor-pointer"
                    >
                      <div>
                        <span className="font-bold text-xs text-foreground group-hover:text-emerald-700 dark:group-hover:text-emerald-400 transition-colors block">
                          {kit.nome}
                        </span>
                        <span className="text-[11px] text-muted-foreground mt-1 line-clamp-2 block leading-relaxed">
                          {kit.descricao}
                        </span>
                      </div>
                      <span className="text-[11px] font-bold text-emerald-600 dark:text-emerald-400 mt-2 flex items-center gap-1">
                        ⚡ Prescrever Kit
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Quick Prescription Type Selector */}
          <div className="space-y-2">
            <label className="text-sm font-semibold text-foreground">Tipo de Receituário</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setPrescriptionType('COMUM')}
                disabled={isDisabled}
                className={`flex items-start gap-3 p-4 rounded-xl border-2 text-left transition-all cursor-pointer ${
                  prescriptionType === 'COMUM'
                    ? 'border-blue-500 bg-blue-50/70 dark:bg-blue-950/40 text-blue-950 dark:text-blue-200 shadow-sm'
                    : 'border-border bg-card text-muted-foreground hover:border-primary/40'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <div className={`p-2 rounded-lg shrink-0 ${prescriptionType === 'COMUM' ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/60 dark:text-blue-300' : 'bg-muted text-muted-foreground'}`}>
                  <Pill className="h-5 w-5" />
                </div>
                <div>
                  <span className="font-bold text-sm block text-foreground">Receituário Simples</span>
                  <span className="text-xs text-muted-foreground">Medicamentos de uso contínuo, antibióticos e rotina</span>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setPrescriptionType('CONTROLADA')}
                disabled={isDisabled}
                className={`flex items-start gap-3 p-4 rounded-xl border-2 text-left transition-all cursor-pointer ${
                  prescriptionType === 'CONTROLADA'
                    ? 'border-purple-500 bg-purple-50/70 dark:bg-purple-950/40 text-purple-950 dark:text-purple-200 shadow-sm'
                    : 'border-border bg-card text-muted-foreground hover:border-primary/40'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <div className={`p-2 rounded-lg shrink-0 ${prescriptionType === 'CONTROLADA' ? 'bg-purple-100 text-purple-600 dark:bg-purple-900/60 dark:text-purple-300' : 'bg-muted text-muted-foreground'}`}>
                  <Syringe className="h-5 w-5" />
                </div>
                <div>
                  <span className="font-bold text-sm block text-foreground">Controle Especial</span>
                  <span className="text-xs text-muted-foreground">Portaria 344/98 - Notificação de Receita / 2 Vias</span>
                </div>
              </button>
            </div>
          </div>

          {/* Optional Additional Observations */}
          <div className="space-y-2">
            <label className="text-sm font-semibold text-foreground">Instruções ou Observações Adicionais (Opcional)</label>
            <textarea
              placeholder="Instruções adicionais para a farmácia ou paciente..."
              value={additionalInstructions}
              onChange={(e) => setAdditionalInstructions(e.target.value)}
              disabled={isDisabled}
              rows={3}
              className="w-full px-4 py-3 border border-border bg-background text-foreground rounded-xl focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 resize-none text-sm"
            />
          </div>

          {/* Local error banner */}
          {localError && (
            <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-xl">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-red-700 dark:text-red-400 font-medium">{localError}</p>
                {localError.includes('Configurações > MeMed') && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => navigate('/medico/configuracoes/memed')}
                    className="mt-2.5 text-xs border-red-300 dark:border-red-800 text-red-800 dark:text-red-300 bg-white dark:bg-red-950 hover:bg-red-50 font-semibold"
                  >
                    Ir para Configurações MeMed
                  </Button>
                )}
              </div>
              <button
                type="button"
                onClick={() => setLocalError(null)}
                className="p-1 text-red-400 hover:text-red-600 hover:bg-red-100 rounded transition-colors cursor-pointer"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          {/* Hero Prescribe Button */}
          <div className="pt-2 space-y-2">
            <button
              type="button"
              onClick={() => handleOpenMemed()}
              disabled={isDisabled || !selectedPatient || missingPatientFields.length > 0}
              className="w-full relative group overflow-hidden flex items-center justify-center gap-3 px-5 py-3.5 sm:px-6 sm:py-4 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-bold text-sm sm:text-base rounded-xl shadow-md hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none cursor-pointer"
            >
              {isInitializingMemed ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span>Inicializando MeMed...</span>
                </>
              ) : isOpeningPrescription || isSettingPatient || isSettingAllergies ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span>Preparando MeMed...</span>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <Pill className="h-5 w-5 text-emerald-100" />
                    <ShieldCheck className="h-5 w-5 text-emerald-100" />
                  </div>
                  <span>Prescrever Medicamentos com MeMed</span>
                </>
              )}
            </button>
            <p className="text-xs text-muted-foreground text-center flex items-center justify-center gap-1.5">
              <ShieldCheck className="h-4 w-4 text-emerald-600 shrink-0" />
              <span>Emissão com assinatura digital ICP-Brasil e base oficial de medicamentos MeMed</span>
            </p>
          </div>
        </div>
      )}

      {/* Botão Flutuante de Fechar Módulo MeMed caso esteja aberto */}
      {isMemedOpen && (
        <button
          type="button"
          onClick={() => {
            try {
              if ((window as any).MdHub?.module?.hide) {
                (window as any).MdHub.module.hide('plataforma.prescricao');
              }
            } catch {
              // Silencioso
            }
            if (typeof document !== 'undefined') {
              document.body.classList.remove('memed-module-open');
            }
            setIsMemedOpen(false);
          }}
          className="fixed top-4 right-4 z-[999999] flex items-center gap-2 px-4 py-2.5 rounded-full bg-slate-900/95 text-white hover:bg-slate-950 border border-white/20 shadow-2xl backdrop-blur-md text-xs font-bold transition-all cursor-pointer hover:scale-105 active:scale-95"
          title="Fechar janela da MeMed"
        >
          <X className="w-4 h-4 text-rose-400" />
          <span>Fechar Prescrição MeMed</span>
        </button>
      )}
    </div>
  );
}
